// src/components/dashboard/DashboardOutcomes.tsx
// MERGED — combines two prior passes that were never reconciled:
//   1. "Outcome" → "package" language correction (H1, buttons, empty state)
//   2. Starter Kit Discovery/Selection wiring (StarterKitDialog) — this
//      part was previously only generated in conversation, never applied
//      to the actual file, per explicit confirmation.
//
// Also fixes a miss from the original language pass: the empty-state
// button read "Define your first outcome" — not caught in that edit
// since it wasn't part of the diff shown at the time. Corrected here
// alongside the kit wiring.
//
// Backend contract unchanged from the CORRECTED version — still calls
// POST /dashboard/outcomes, /dashboard/outcomes/status. Starter Kit
// install itself happens inside StarterKitDialog.tsx via
// POST /dashboard/outcomes/install-kit — NOT YET APPLIED to the backend
// per your confirmation (registry.ts, install-starter-kit.ts,
// _toolTable.ts, _toolRegistry.ts, dashboard.ts entries all still only
// exist as generated code in conversation). This file will render and
// the dialog will open, but clicking "Install this kit" inside it will
// fail until the backend half is actually applied — do not treat this
// file's presence as evidence the feature works end-to-end.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Plus, Sparkles } from 'lucide-react';
import { authedFetch } from '../../services/dashboardApi';
import { StarterKitDialog } from './StarterKitDialog';

type OutcomeStatus = 'draft' | 'live' | 'paused';
type WorkMode = 'observe' | 'ask_first' | 'complete_within_limits';

interface PriceModel {
  kind: 'fixed' | 'hourly' | 'range';
  amountCents: number | null;
  minCents: number | null;
  maxCents: number | null;
}

interface Outcome {
  id: string;
  name: string;
  description: string;
  completionCriteria: string | null;
  priceModel: PriceModel;
  priceUnit: string | null;
  defaultWorkMode: WorkMode;
  linkedCapabilityId: string | null;
  status: OutcomeStatus;
}

const STATUS_STYLES: Record<OutcomeStatus, string> = {
  draft: 'bg-brand-surface text-brand-gray',
  live: 'bg-green-50 text-green-700',
  paused: 'bg-amber-50 text-amber-700',
};

const STATUS_LABELS: Record<OutcomeStatus, string> = {
  draft: 'Draft',
  live: 'Live',
  paused: 'Paused',
};

const WORK_MODE_LABELS: Record<WorkMode, string> = {
  observe: 'Observe',
  ask_first: 'Ask First',
  complete_within_limits: 'Complete Within Limits',
};

function formatPrice(priceModel: PriceModel, priceUnit: string | null): string | null {
  const unit = priceUnit ? ` ${priceUnit}` : '';
  if (priceModel.kind === 'fixed' && priceModel.amountCents != null) {
    return `$${(priceModel.amountCents / 100).toLocaleString()}${unit}`;
  }
  if (priceModel.kind === 'hourly' && priceModel.amountCents != null) {
    return `$${(priceModel.amountCents / 100).toLocaleString()}/hr`;
  }
  if (priceModel.kind === 'range' && priceModel.minCents != null && priceModel.maxCents != null) {
    return `$${(priceModel.minCents / 100).toLocaleString()}–$${(priceModel.maxCents / 100).toLocaleString()}${unit}`;
  }
  return null;
}

export const DashboardOutcomes: React.FC = () => {
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null);
  const [kitDialogOpen, setKitDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await authedFetch('/outcomes', { method: 'POST' });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to load outcomes.');
        }
        const data = await res.json();
        if (!cancelled) setOutcomes(data.outcomes ?? []);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load your outcomes.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleStatus = async (id: string, current: OutcomeStatus) => {
    const next: OutcomeStatus = current === 'live' ? 'paused' : 'live';
    setPendingStatusChange(id);

    setOutcomes((prev) => prev.map((o) => (o.id === id ? { ...o, status: next } : o)));

    try {
      const res = await authedFetch('/outcomes/status', {
        method: 'POST',
        body: JSON.stringify({ id, status: next }),
      });
      if (!res.ok) throw new Error('Failed to update status.');
    } catch (err: any) {
      setOutcomes((prev) => prev.map((o) => (o.id === id ? { ...o, status: current } : o)));
      setError(err.message || 'Could not update this outcome.');
    } finally {
      setPendingStatusChange(null);
    }
  };

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading outcomes…</p>;

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-brand-black">Your work packages</h1>
            <p className="text-sm text-brand-charcoal font-light mt-1">
              Packages are the scoped pieces of work agents can discover and request under your rules.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setKitDialogOpen(true)} className="flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" />
              Start with a kit
            </Button>
            <Button
              variant="primary"
              size="sm"
              render={<Link to="/dashboard/outcomes/new" className="flex items-center gap-1.5" />}
            >
              <Plus className="w-4 h-4" />
              Create package
            </Button>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {outcomes.length === 0 && !error && (
          <div className="rounded-xl border border-dashed border-brand-border bg-brand-surface p-10 text-center">
            <p className="text-sm text-brand-charcoal font-light mb-4">
              You haven't created a work package yet. Start with a pre-built kit, or build one from scratch — either way, everything stays in draft until you publish it.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="primary" size="sm" onClick={() => setKitDialogOpen(true)}>
                Start with a kit
              </Button>
              <Button variant="outline" size="sm" render={<Link to="/dashboard/outcomes/new" />}>
                Or build one from scratch
              </Button>
            </div>
          </div>
        )}

        {outcomes.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {outcomes.map((o) => {
              const priceLabel = formatPrice(o.priceModel, o.priceUnit);
              return (
                <Link
                  key={o.id}
                  to={`/dashboard/outcomes/${o.id}`}
                  className="block rounded-xl border border-brand-border bg-brand-white p-6 hover:border-brand-gray transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-base font-medium text-brand-black">{o.name}</h3>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-mono shrink-0 ml-3 ${STATUS_STYLES[o.status]}`}>
                      {STATUS_LABELS[o.status]}
                    </span>
                  </div>

                  <p className="text-sm text-brand-charcoal font-light leading-relaxed mb-4 line-clamp-2">
                    {o.description}
                  </p>

                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-brand-gray font-mono mb-4">
                    {priceLabel && <span>{priceLabel}</span>}
                    <span>{WORK_MODE_LABELS[o.defaultWorkMode]}</span>
                  </div>

                  <div className="flex items-center justify-end pt-4 border-t border-brand-border">
                    {o.status !== 'draft' && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleStatus(o.id, o.status);
                        }}
                        disabled={pendingStatusChange === o.id}
                        className="text-xs font-medium text-brand-charcoal hover:text-brand-black underline disabled:opacity-50"
                      >
                        {pendingStatusChange === o.id
                          ? 'Updating…'
                          : o.status === 'live' ? 'Pause' : 'Publish'}
                      </button>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <StarterKitDialog open={kitDialogOpen} onOpenChange={setKitDialogOpen} />
    </>
  );
};